import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSeenTimelineAttentionFingerprint, setSeenTimelineAttentionFingerprint } from '@/helpers/localStorageHelper';
import type { TimelineEventsAPIRequest } from '@/middlewares/paginationApi';
import { useGetTimelineEventsHistoryQuery } from '@/middlewares/paginationApi';
import type { RootState } from '@/reducers/reducers';
import {
    selectIsAppsDemoModeEnabled,
    selectRelease,
    selectShouldHideTimelineAttentionBadge,
} from '@/selectors/selectors';
import { getTimelineAttentionFingerprint, getTimelineEventsNeedingAttention } from './timelineEventHelpers';

interface UseTimelineAttentionProps {
    orgId: string;
    appId: string;
    releaseId: string;
    isViewingTimeline: boolean;
    skip?: boolean;
}

interface UseTimelineAttentionReturn {
    needsAttention: boolean;
    attentionCount: number;
}

export const useTimelineAttention = ({
    orgId,
    appId,
    releaseId,
    isViewingTimeline,
    skip = false,
}: UseTimelineAttentionProps): UseTimelineAttentionReturn => {
    const isBadgeHidden = useSelector((state: RootState) => selectShouldHideTimelineAttentionBadge(state, appId));
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));

    // Same args as the release Timeline page's unfiltered request so both share one cache entry.
    const request = {
        orgId,
        appId,
        releaseId,
        timelineEventFilters: [],
        shouldShowAutomationsTimelineEventsOnly: false,
    };
    const isQuerySkipped = skip || isBadgeHidden || isViewingAppsInDemoMode || !releaseId;
    const { currentData, refetch, isUninitialized } = useGetTimelineEventsHistoryQuery(
        request as TimelineEventsAPIRequest,
        { skip: isQuerySkipped },
    );

    // Nothing invalidates this query, and blocked/failed notes appear on a backend check cycle long after the
    // page loads, so follow the release's render timestamp like the other timeline consumers do.
    const releaseRenderedAt = useSelector((state: RootState) => selectRelease(state, releaseId)?.RenderedAt);
    useEffect(() => {
        if (releaseRenderedAt && !isQuerySkipped && !isUninitialized) {
            refetch();
        }
        // Only a new render should refetch; depending on the query state would double the initial request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [releaseRenderedAt]);

    const attentionEvents = useMemo(
        () => getTimelineEventsNeedingAttention(currentData?.data?.events ?? []),
        [currentData],
    );
    const fingerprint = getTimelineAttentionFingerprint(attentionEvents);
    const [seenFingerprint, setSeenFingerprint] = useState(() => getSeenTimelineAttentionFingerprint(releaseId));

    useEffect(() => {
        setSeenFingerprint(getSeenTimelineAttentionFingerprint(releaseId));
    }, [releaseId]);

    useEffect(() => {
        if (isViewingTimeline && fingerprint) {
            setSeenTimelineAttentionFingerprint(releaseId, fingerprint);
            setSeenFingerprint(fingerprint);
        }
    }, [isViewingTimeline, fingerprint, releaseId]);

    return {
        needsAttention: !isBadgeHidden && fingerprint !== '' && fingerprint !== seenFingerprint,
        attentionCount: attentionEvents.length,
    };
};
